import { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions
// ============================================================================
//
// Each frequently asked question is its own slot group of a `question` (text)
// and an `answer` (richtext) field, so an editor can hand-author every Q&A
// pair. There is no FAQ property collection to bind to, so the fields carry
// representative `defaultValue` copy as the non-property fallback. Written as
// fully-inlined object literals (no shared factory / referenced consts) so the
// preview system's static schema extractor can resolve them.

const faq1Def: SlotGroupDefinition = {
  label: 'Question 1',
  fields: {
    question: { label: 'Question', type: 'text', defaultValue: 'How do I schedule a tour?' },
    answer: {
      label: 'Answer',
      type: 'richtext',
      defaultValue:
        '<p>You can schedule a tour directly from our website using the “Schedule A Tour” page, or by calling our leasing office. We offer in-person, self-guided, and virtual tour options to fit your schedule.</p>',
    },
  },
};

const faq2Def: SlotGroupDefinition = {
  label: 'Question 2',
  fields: {
    question: { label: 'Question', type: 'text', defaultValue: 'What is the application process?' },
    answer: {
      label: 'Answer',
      type: 'richtext',
      defaultValue:
        '<p>Once you’ve found your ideal floor plan, you can apply online in just a few minutes. We’ll ask for basic personal and income information, and our team will guide you through each step toward move-in.</p>',
    },
  },
};

const faq3Def: SlotGroupDefinition = {
  label: 'Question 3',
  fields: {
    question: { label: 'Question', type: 'text', defaultValue: 'Are pets allowed?' },
    answer: {
      label: 'Answer',
      type: 'richtext',
      defaultValue:
        '<p>Yes — we’re a pet-friendly community and welcome both cats and dogs. Please review our Pet Policy page for details on breed restrictions, weight limits, and any applicable pet fees or deposits.</p>',
    },
  },
};

const faq4Def: SlotGroupDefinition = {
  label: 'Question 4',
  fields: {
    question: { label: 'Question', type: 'text', defaultValue: 'What utilities are included in my rent?' },
    answer: {
      label: 'Answer',
      type: 'richtext',
      defaultValue:
        '<p>Utility inclusions vary by floor plan and lease term. Our leasing team can walk you through exactly which utilities are covered and which are billed separately so there are no surprises.</p>',
    },
  },
};

const faq5Def: SlotGroupDefinition = {
  label: 'Question 5',
  fields: {
    question: { label: 'Question', type: 'text', defaultValue: 'Is parking available?' },
    answer: {
      label: 'Answer',
      type: 'richtext',
      defaultValue:
        '<p>Yes, we offer resident parking options. Availability and pricing depend on the type of space you choose. Reach out to our leasing office to learn more about current parking availability.</p>',
    },
  },
};

const faq6Def: SlotGroupDefinition = {
  label: 'Question 6',
  fields: {
    question: { label: 'Question', type: 'text', defaultValue: 'How do I submit a maintenance request?' },
    answer: {
      label: 'Answer',
      type: 'richtext',
      defaultValue:
        '<p>Current residents can submit and track maintenance requests any time through the online resident portal. For urgent or emergency issues, please contact the leasing office directly.</p>',
    },
  },
};

const faq7Def: SlotGroupDefinition = {
  label: 'Question 7',
  fields: {
    question: { label: 'Question', type: 'text', defaultValue: 'What lease terms do you offer?' },
    answer: {
      label: 'Answer',
      type: 'richtext',
      defaultValue:
        '<p>We offer a range of lease terms to fit your lifestyle, with standard 12-month leases as well as select short-term options. Our leasing team can help you find the term and pricing that work best for you.</p>',
    },
  },
};

const faq8Def: SlotGroupDefinition = {
  label: 'Question 8',
  fields: {
    question: { label: 'Question', type: 'text', defaultValue: 'How much is the security deposit?' },
    answer: {
      label: 'Answer',
      type: 'richtext',
      defaultValue:
        '<p>Security deposit amounts vary based on your floor plan and application. Once your application is approved, we’ll provide a full breakdown of any deposits and fees before you sign your lease.</p>',
    },
  },
};

const faq9Def: SlotGroupDefinition = {
  label: 'Question 9',
  fields: {
    question: { label: 'Question', type: 'text', defaultValue: 'What do I need to move in?' },
    answer: {
      label: 'Answer',
      type: 'richtext',
      defaultValue:
        '<p>Before move-in day, we’ll confirm your signed lease, first month’s rent, any required deposits, and proof of renters insurance. Our team will send a simple move-in checklist so everything is ready when you arrive.</p>',
    },
  },
};

const faq10Def: SlotGroupDefinition = {
  label: 'Question 10',
  fields: {
    question: { label: 'Question', type: 'text', defaultValue: 'Can guests use the community amenities?' },
    answer: {
      label: 'Answer',
      type: 'richtext',
      defaultValue:
        '<p>Residents are welcome to enjoy our community amenities, and guests may join you in most shared spaces when accompanied. Some amenities have specific hours or guest guidelines — check with the leasing office for details.</p>',
    },
  },
};

// ============================================================================
// Presentational accordion item (no hooks — lives inside each EditableSlotGroup)
// ============================================================================

interface FAQItemProps {
  question: string;
  answerHtml: string;
  index: number;
  isOpen: boolean;
  onToggle: (index: number) => void;
}

function FAQItem({ question, answerHtml, index, isOpen, onToggle }: FAQItemProps) {
  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-question-${index}`;

  return (
    <div className="py-1">
      <h3 className="m-0">
        <button
          type="button"
          id={buttonId}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => onToggle(index)}
          className="flex w-full items-center justify-between gap-4 py-5 text-left font-heading text-lg sm:text-xl font-semibold text-[var(--vibe-color-text)] transition-colors hover:text-[var(--vibe-color-primary)]"
        >
          <span>{question}</span>
          <svg
            className="h-5 w-5 flex-shrink-0 text-[var(--vibe-color-primary)]"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            aria-hidden="true"
          >
            {/* Horizontal bar — always visible */}
            <path d="M4 10 16 10" strokeLinecap="round" />
            {/* Vertical bar — hidden when open, so "+" becomes "−" (close) */}
            <path
              d="M10 4 10 16"
              strokeLinecap="round"
              className={`origin-center transition-all duration-300 ${isOpen ? 'rotate-90 opacity-0' : 'rotate-0 opacity-100'}`}
            />
          </svg>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!isOpen}
        className="pb-5 pr-8 font-body text-base leading-relaxed text-[var(--vibe-color-text)]/75 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1"
        dangerouslySetInnerHTML={{ __html: answerHtml }}
      />
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FAQList() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const faq1 = useSlotGroup('faqlist.item_1', faq1Def);
  const faq2 = useSlotGroup('faqlist.item_2', faq2Def);
  const faq3 = useSlotGroup('faqlist.item_3', faq3Def);
  const faq4 = useSlotGroup('faqlist.item_4', faq4Def);
  const faq5 = useSlotGroup('faqlist.item_5', faq5Def);
  const faq6 = useSlotGroup('faqlist.item_6', faq6Def);
  const faq7 = useSlotGroup('faqlist.item_7', faq7Def);
  const faq8 = useSlotGroup('faqlist.item_8', faq8Def);
  const faq9 = useSlotGroup('faqlist.item_9', faq9Def);
  const faq10 = useSlotGroup('faqlist.item_10', faq10Def);

  // Open the first question by default; clicking a question toggles its panel.
  const [openIndex, setOpenIndex] = useState<number>(0);
  const toggle = (index: number) => setOpenIndex((cur) => (cur === index ? -1 : index));

  const q = (value: string | null | undefined, def: SlotGroupDefinition) =>
    value?.trim() || def.fields.question.defaultValue || '';
  const a = (value: string | null | undefined, def: SlotGroupDefinition) =>
    value || def.fields.answer.defaultValue || '';

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up pb-16 pt-4 md:pb-24 md:pt-6 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="divide-y divide-[color-mix(in_srgb,var(--vibe-color-text)_15%,transparent)] border-y border-[color-mix(in_srgb,var(--vibe-color-text)_15%,transparent)]">
          <EditableSlotGroup groupPath="faqlist.item_1" definition={faq1Def}>
            <FAQItem question={q(faq1.question, faq1Def)} answerHtml={a(faq1.answer, faq1Def)} index={0} isOpen={openIndex === 0} onToggle={toggle} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="faqlist.item_2" definition={faq2Def}>
            <FAQItem question={q(faq2.question, faq2Def)} answerHtml={a(faq2.answer, faq2Def)} index={1} isOpen={openIndex === 1} onToggle={toggle} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="faqlist.item_3" definition={faq3Def}>
            <FAQItem question={q(faq3.question, faq3Def)} answerHtml={a(faq3.answer, faq3Def)} index={2} isOpen={openIndex === 2} onToggle={toggle} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="faqlist.item_4" definition={faq4Def}>
            <FAQItem question={q(faq4.question, faq4Def)} answerHtml={a(faq4.answer, faq4Def)} index={3} isOpen={openIndex === 3} onToggle={toggle} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="faqlist.item_5" definition={faq5Def}>
            <FAQItem question={q(faq5.question, faq5Def)} answerHtml={a(faq5.answer, faq5Def)} index={4} isOpen={openIndex === 4} onToggle={toggle} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="faqlist.item_6" definition={faq6Def}>
            <FAQItem question={q(faq6.question, faq6Def)} answerHtml={a(faq6.answer, faq6Def)} index={5} isOpen={openIndex === 5} onToggle={toggle} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="faqlist.item_7" definition={faq7Def}>
            <FAQItem question={q(faq7.question, faq7Def)} answerHtml={a(faq7.answer, faq7Def)} index={6} isOpen={openIndex === 6} onToggle={toggle} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="faqlist.item_8" definition={faq8Def}>
            <FAQItem question={q(faq8.question, faq8Def)} answerHtml={a(faq8.answer, faq8Def)} index={7} isOpen={openIndex === 7} onToggle={toggle} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="faqlist.item_9" definition={faq9Def}>
            <FAQItem question={q(faq9.question, faq9Def)} answerHtml={a(faq9.answer, faq9Def)} index={8} isOpen={openIndex === 8} onToggle={toggle} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="faqlist.item_10" definition={faq10Def}>
            <FAQItem question={q(faq10.question, faq10Def)} answerHtml={a(faq10.answer, faq10Def)} index={9} isOpen={openIndex === 9} onToggle={toggle} />
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {},
  slotGroups: {
    'faqlist.item_1': faq1Def,
    'faqlist.item_2': faq2Def,
    'faqlist.item_3': faq3Def,
    'faqlist.item_4': faq4Def,
    'faqlist.item_5': faq5Def,
    'faqlist.item_6': faq6Def,
    'faqlist.item_7': faq7Def,
    'faqlist.item_8': faq8Def,
    'faqlist.item_9': faq9Def,
    'faqlist.item_10': faq10Def,
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
